import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  type ExternalLinkParsed,
  type ExternalLink,
  type ExternalLinkSettings,
  type ExternalLinkFormData,
} from "../types/externalLinkTypes";
import { ReducerNamespaceTypes, ReducerEntityPrefixTypes } from "../../../../enums";
import { type TagsPayloadCamelPlural } from "interfaces/Tags";
import { type PeopleGroupAssignment } from "features/Library/PeopleAssignments/Overview/helpers/getColumnOptions";
import { type LinkUser } from "../types/models";
import { type RootState } from "features/Application/globaltypes/redux";
import { mapTagsToView } from "features/Library/Common/helpers/tagsHelper";

export interface ExternalLinkDetailsState {
  link: ExternalLinkParsed;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  tags: {
    tags: TagsPayloadCamelPlural;
    error?: Error;
  };
  groups: {
    items: PeopleGroupAssignment[];
    count: number;
    isLoading: boolean;
  };
  users: {
    items: LinkUser[];
    count: number;
    isLoading: boolean;
  };
}

export const initialState: ExternalLinkDetailsState = {
  link: {
    id: -1,
    title: "",
    description: "",
    url: "",
    urlText: "",
    ownerId: -1,
    publisherId: -1,
    isDraft: true,
    dateCreated: "",
    dateModified: "",
    hasBeenPublished: false,
    isPurchased: false,
    settings: {
      labels: [],
      softwareApplications: [],
      flows: [],
      packs: [],
    },
  },
  isLoading: false,
  isLoaded: false,
  error: null,
  tags: {
    tags: {},
    error: undefined,
  },
  groups: {
    items: [],
    count: 0,
    isLoading: false,
  },
  users: {
    items: [],
    count: 0,
    isLoading: false,
  },
};

const externalLinkDetailsSlice = createSlice({
  name: `${ReducerNamespaceTypes.Library}/${ReducerEntityPrefixTypes.ExternalLinks}/details`,
  initialState,
  reducers: {
    getLink(state) {
      state.isLoading = true;
      state.isLoaded = false;
      state.error = null;
    },
    saveLink(state, action: PayloadAction<ExternalLinkFormData>) {
      state.link.title = action.payload.title;
      state.link.description = action.payload.description;
      state.link.url = action.payload.url;
      state.link.urlText = action.payload.urlText;
    },
    setLink(state, action: PayloadAction<ExternalLink>) {
      // Convert ExternalLink to ExternalLinkParsed
      const settings = {
        labels: action.payload.labels,
        softwareApplications: action.payload.softwareApplications,
        flows: action.payload.flows,
        packs: action.payload.packs,
      };
      state.link = { ...action.payload, settings };
      state.isLoading = false;
      state.isLoaded = true;
    },
    getLinkFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetLinkDetails: () => initialState,
    saveSettings(state, action: PayloadAction<ExternalLinkSettings>) {
      if (state.link) {
        Object.assign(state.link.settings, action.payload);
      }
    },
    setTags(state, action: PayloadAction<TagsPayloadCamelPlural>) {
      state.tags.tags = action.payload;
      state.tags.error = undefined;
    },
    setTagsError(state, action: PayloadAction<Error>) {
      state.tags.error = action.payload;
    },
    fetchGroupsStart(state) {
      state.groups.isLoading = true;
      state.error = null;
    },
    setGroups(state, action: PayloadAction<{ items: PeopleGroupAssignment[]; count: number }>) {
      state.groups.items = action.payload.items;
      state.groups.isLoading = false;
      state.groups.count = action.payload.count;
    },
    setGroupsFailure(state, action: PayloadAction<string>) {
      state.groups.items = [];
      state.groups.count = 0;
      state.groups.isLoading = false;
      state.error = action.payload;
    },
    fetchUsersStart(state) {
      state.users.isLoading = true;
      state.error = null;
    },
    setUsers(state, action: PayloadAction<{ items: LinkUser[]; count: number }>) {
      state.users.items = action.payload.items;
      state.users.isLoading = false;
      state.users.count = action.payload.count;
    },
    setUsersFailure(state, action: PayloadAction<string>) {
      state.users.items = [];
      state.users.count = 0;
      state.users.isLoading = false;
      state.error = action.payload;
    },
    resetTags: (state: ExternalLinkDetailsState) => {
      state.tags = initialState.tags;
    },
  },
});

export const {
  getLink,
  setLink,
  saveLink,
  getLinkFailure,
  resetLinkDetails,
  saveSettings,
  setTags,
  setTagsError,
  fetchGroupsStart,
  setGroups,
  setGroupsFailure,
  fetchUsersStart,
  setUsers,
  setUsersFailure,
} = externalLinkDetailsSlice.actions;

export const normalizedTagsSelector = createSelector(
  [(state: RootState): TagsPayloadCamelPlural => state.library.externalLinks.details.tags.tags],
  mapTagsToView,
);

export default externalLinkDetailsSlice.reducer;
